import { DISCARD_REASONS, DISCARD_REASON_LABELS, type DiscardReason, type DiscardTicketInput } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useId, useState, type FormEvent } from 'react';

/** Che cosa significa ciascun motivo, sotto l'etichetta. */
const REASON_HINTS: Record<DiscardReason, string> = {
  duplicato: 'La stessa segnalazione è già in un altro ticket: indica quale.',
  spam: 'Non è una segnalazione: pubblicità, prove, messaggi automatici.',
  non_pertinente: 'Non riguarda il prodotto o non è un problema del supporto.',
};

export interface DiscardTicketDialogProps {
  open: boolean;
  code: string;
  busy: boolean;
  error: string | null;
  onConfirm: (input: DiscardTicketInput) => void;
  onClose: () => void;
}

/**
 * «Scarta» (DEC-002 regola 3): motivo obbligatorio fra duplicato, spam e non pertinente; per un
 * duplicato il codice del ticket originale. Nessuna email a chi ha segnalato: il dialogo lo dice.
 * Smontato alla chiusura, così ogni apertura riparte vuota.
 */
export function DiscardTicketDialog({ open, code, busy, error, onConfirm, onClose }: DiscardTicketDialogProps) {
  const [reason, setReason] = useState<DiscardReason | null>(null);
  const [original, setOriginal] = useState('');
  const titleId = useId();
  const reasonId = useId();
  const needsOriginal = reason === 'duplicato';
  const originalCode = original.trim().toUpperCase();
  const sameTicket = needsOriginal && originalCode === code;
  const canConfirm = reason !== null && (!needsOriginal || (originalCode !== '' && !sameTicket)) && !busy;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canConfirm || reason === null) return;
    onConfirm(needsOriginal ? { reason, original: originalCode } : { reason });
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} aria-labelledby={titleId} fullWidth maxWidth="xs">
      <form noValidate onSubmit={submit}>
        <DialogTitle id={titleId}>Scarta il ticket {code}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <DialogContentText>
              Il ticket passa a «Scartato», che è definitivo. Chi ha segnalato non riceve nessuna email; gli scartati
              sono contati a parte nelle statistiche.
            </DialogContentText>
            <FormLabel id={reasonId} required>
              Motivo
            </FormLabel>
            <RadioGroup
              aria-labelledby={reasonId}
              aria-required="true"
              value={reason ?? ''}
              onChange={(event) => setReason(event.target.value as DiscardReason)}
            >
              {DISCARD_REASONS.map((value) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  control={<Radio />}
                  label={
                    <span>
                      {DISCARD_REASON_LABELS[value]}
                      <br />
                      <small>{REASON_HINTS[value]}</small>
                    </span>
                  }
                />
              ))}
            </RadioGroup>
            {needsOriginal && (
              <TextField
                label="Codice del ticket originale"
                placeholder="es. EC-2026-0001"
                value={original}
                onChange={(event) => setOriginal(event.target.value)}
                error={sameTicket}
                helperText={
                  sameTicket ? 'Indica un ticket diverso da questo.' : 'Il ticket di cui questo è un duplicato.'
                }
                required
              />
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button type="submit" color="error" variant="contained" disabled={!canConfirm}>
            {busy ? 'Scarto…' : 'Scarta'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
